/**
 * Pure utility for extracting structured address fields from a Google Maps
 * Places `PlaceResult`. Keeps map-provider details out of the UI component.
 *
 * Missing fields default to empty strings (never `undefined`) so downstream
 * form state stays predictable. Coordinates are `null` when geometry is
 * absent.
 */

// Minimal inline typing — `@types/google.maps` is not installed. Mirror the
// approach in `mapsLoader.ts`: describe only the shape we consume.
interface AddressComponent {
  long_name?: string;
  short_name?: string;
  types?: string[];
}

interface PlaceGeometry {
  location?: {
    lat: () => number;
    lng: () => number;
  };
}

interface PlaceResultLike {
  address_components?: AddressComponent[];
  formatted_address?: string;
  geometry?: PlaceGeometry;
}

export interface ParsedPlace {
  /** `"<street_number> <route>"` trimmed, or empty string when both missing. */
  addressLine1: string;
  /** `locality`, falling back to `sublocality_level_1`. */
  city: string;
  /** `administrative_area_level_1`. */
  state: string;
  /** `postal_code`. */
  postalCode: string;
  /** ISO-3166 alpha-2 (`short_name` of the country component). */
  country: string;
  /** `place.formatted_address`. */
  formattedAddress: string;
  latitude: number | null;
  longitude: number | null;
}

function findComponent(
  components: AddressComponent[] | undefined,
  type: string,
): AddressComponent | undefined {
  if (!components) return undefined;
  return components.find((c) => Array.isArray(c.types) && c.types.includes(type));
}

function longName(
  components: AddressComponent[] | undefined,
  type: string,
): string {
  return findComponent(components, type)?.long_name ?? '';
}

function shortName(
  components: AddressComponent[] | undefined,
  type: string,
): string {
  return findComponent(components, type)?.short_name ?? '';
}

export function parsePlace(place: PlaceResultLike): ParsedPlace {
  const components = place?.address_components;

  const streetNumber = longName(components, 'street_number');
  const route = longName(components, 'route');
  const addressLine1 = `${streetNumber} ${route}`.trim();

  // City resolution order:
  //   1. `locality`            — works for US/EU/most countries (e.g. "New York")
  //   2. `administrative_area_level_1` — KR/JP/CN often expose the actual
  //      city name here (e.g. "서울특별시", "東京都") with `locality`
  //      empty.
  // We deliberately do NOT fall back to `sublocality_level_1` because in
  // Korea that's the district level (구로구, 관악구), which is not what we
  // want to surface as the city.
  const locality = longName(components, 'locality');
  const adminArea1 = longName(components, 'administrative_area_level_1');
  const city = locality || adminArea1;

  const state = adminArea1;
  const postalCode = longName(components, 'postal_code');
  const country = shortName(components, 'country');

  const location = place?.geometry?.location;
  const latitude = typeof location?.lat === 'function' ? location.lat() : null;
  const longitude = typeof location?.lng === 'function' ? location.lng() : null;

  return {
    addressLine1,
    city,
    state,
    postalCode,
    country,
    formattedAddress: place?.formatted_address ?? '',
    latitude,
    longitude,
  };
}
